import PeekEventContent from './PeekEventContent';
import { observer } from 'mobx-react-lite';
import { ViewType } from 'owa-calendar-actions';
import logEventViewedDatapoint from 'owa-calendar-forms-common/lib/helpers/logEventViewedDatapoint';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import EventScope from 'owa-service/lib/contract/EventScope';
import ResponseTypeType from 'owa-service/lib/contract/ResponseTypeType';
import { TxpPeekWrapper } from 'owa-txp';
import areAnyTXPEntitiesSupported from 'owa-txp-common/lib/utils/featureChecks/areAnyTXPEntitiesSupported';
import isInvoiceSupported from 'owa-txp-common/lib/utils/featureChecks/isInvoiceSupported';
import TxpSource from 'owa-txp/lib/store/viewSchema/TxpSource';
import * as React from 'react';
import { CalendarComponent } from 'owa-calendar-usage-instrumentation';

export interface PeekContentSelectorProps {
    event: CalendarEvent;
    colorScheme: ColorScheme;

    //TxpPeekWrapper
    txpSource: TxpSource;
    onViewInMailClicked?: () => void;

    // PeekEventContent
    viewType: ViewType;
    onEditClicked: (eventScope: EventScope) => void;
    onDeleteClicked: (eventScope: EventScope) => void;
    onRespondClicked: (
        newResponseType: ResponseTypeType,
        eventScope: EventScope,
        sendMessage: boolean,
        notes: string
    ) => void;
    onDismiss?: () => void;
    onAttendeeTrackingClicked?: () => void;
    onReviewProposedNewTimeClicked?: (event?: Partial<CalendarEvent>) => void;
    allowFullBody: boolean;
    showInlineCommandBar: boolean;
    onOpenEventClicked?: (
        ev?: React.MouseEvent<HTMLElement>,
        shouldScrollToInsights?: boolean
    ) => void;
}

const PeekContentSelector: React.FunctionComponent<PeekContentSelectorProps> = props => {
    const {
        event,
        colorScheme,
        txpSource,
        onViewInMailClicked,
        viewType,
        onEditClicked,
        onDeleteClicked,
        onRespondClicked,
        onDismiss,
        onAttendeeTrackingClicked,
        onReviewProposedNewTimeClicked,
        allowFullBody,
        showInlineCommandBar,
        onOpenEventClicked,
    } = props;

    React.useEffect(() => {
        logEventViewedDatapoint(viewType, event, getCalendarComponent(viewType));
    }, []);

    if (areAnyTXPEntitiesSupported(event.EntityNamesMap)) {
        return (
            <TxpPeekWrapper
                colorScheme={colorScheme}
                entityNamesMap={event.EntityNamesMap}
                txpBlob={event.TailoredXpEntities}
                txpLocation={event.Location}
                source={txpSource}
                onViewInMailClicked={onViewInMailClicked}
                isInvoiceSupported={isInvoiceSupported(event.EntityNamesMap)}
            />
        );
    } else {
        return (
            <PeekEventContent
                item={event}
                colorScheme={colorScheme}
                actionSource={viewType}
                onEditClicked={onEditClicked}
                onDeleteClicked={onDeleteClicked}
                onRespondClicked={onRespondClicked}
                onAttendeeTrackingClicked={onAttendeeTrackingClicked}
                onReviewProposedNewTimeClicked={onReviewProposedNewTimeClicked}
                onDismiss={onDismiss}
                allowFullBody={allowFullBody}
                showInlineCommandBar={showInlineCommandBar}
                onOpenEventClicked={onOpenEventClicked}
            />
        );
    }
};

function getCalendarComponent(viewType: ViewType): CalendarComponent {
    switch (viewType) {
        case 'SurfacePeek':
            return CalendarComponent.CalendarItemPeek;
        case 'TimePanelPeek':
            return CalendarComponent.MyDayPanelEventView;
        default:
            return CalendarComponent.Unknown;
    }
}

export default observer(PeekContentSelector);
